import { useContext, useMemo, useState } from "react";
import { AdministrarContext } from "./context";
import { Autocomplete, Grid, Icon, InputLabel, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import DinamicCard from "components/Cards/InfoCards/DinamicCard";
import SoftButton from "components/SoftButton";
import { formatDate } from "utils/formatters";
import ModalDetalleLicitacion from "./ModalDetalleLicitacion";
import { gql, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { UserContext } from "context/user";
import { PERMISOS_COMPRAS, PERMISOS_CREAR_SOLICITUD, tienePermisoCompras } from "layouts/compras/permisos";
import {
  TIPOS_COMPRA,
  describirIntervalo,
  puedeEditarDatosGenerales,
  puedeCambiarTipoCompra,
} from "./reglasPedido";

export default function Tab0(args) {
  const { data, setData, getError, marcarTocado, params, refetch, refetchParent, mode } =
    useContext(AdministrarContext);
  const [openIniciarLicitacion, setOpenIniciarLicitacion] = useState(false);
  const { user: currentUser, verificarAcceso } = useContext(UserContext);
  const puedeElegirResponsable = verificarAcceso(PERMISOS_COMPRAS.ADMINISTRAR_EXPEDIENTES);

  const { data: dataUsers } = useQuery(gql`
    query getUsers {
      usuarios(filter: { tipoUser: "Empleado" }, order: { field: "nombre", order: ASC }) {
        id
        nombre
        apellido
      }
    }
  `);

  const handleChange = (e) => {
    const { name, value } = e.target;
    marcarTocado(name);

    // Si cambia el tipo de compra, resetear campos del pliego particular
    if (name === "tipoCompra") {
      setData((data) => ({
        ...data,
        [name]: value,
        pliegoVencimientoDias: null,
        pliegoRequiereDemanda: null,
        pliegoParticularidades: null,
      }));
    } else {
      setData((data) => ({ ...data, [name]: value }));
    }
  };

  const allowProcesoCotizacion = () => {
    if (
      data?.id &&
      Boolean(data?.tipoCompra) &&
      data?.tipoCompra != "Directa" &&
      (data.stockEstado == "Verificado" || data.tipoDetalle == "Servicios") &&
      data.estados?.find((e) => e.base.orden == 4)?.finalizado
    ) {
      return true;
    }
    return false;
  };

  const selectedResponsable = useMemo(() => {
    return (
      dataUsers?.usuarios?.find((u) => u.id == (data?.idResponsable || data?.responsable?.id)) ||
      null
    );
  }, [dataUsers, data?.idResponsable, data?.responsable?.id]);

  const allowEdit = useMemo(
    () => puedeEditarDatosGenerales(data, mode, currentUser),
    [data?.estados, data?.id, data?.createdUser, mode, currentUser]
  );

  const allowTipoCompra = puedeCambiarTipoCompra(data, {
    puedeEditar: allowEdit,
    puedeCrearSolicitud: verificarAcceso(PERMISOS_CREAR_SOLICITUD, "or"),
  });

  const montos = params?.["MONTOS_PRESUPUESTOS"];
  const tipoCompraActual = TIPOS_COMPRA[data?.tipoCompra];

  return (
    <Grid container spacing={1} lineHeight={1}>
      <Grid item xs={allowProcesoCotizacion() ? 8 : 12}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={8}>
            <InputLabel htmlFor="Nombre">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Nombre*
              </SoftTypography>
            </InputLabel>
            {allowEdit ? (
              <Tooltip title={getError("nombre") || ""} placement="right">
                <SoftInput
                  label="Nombre"
                  placeholder="Nombre descriptivo"
                  type="text"
                  name="nombre"
                  error={Boolean(getError("nombre"))}
                  value={data?.nombre || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: 254 }}
                />
              </Tooltip>
            ) : (
              <SoftTypography variant="caption">{data?.nombre ?? "-"}</SoftTypography>
            )}
          </Grid>
          <Grid item xs={12} sm={5}>
            <InputLabel htmlFor="tipoCompra">
              <SoftBox display="flex" alignItems="center" gap={1} pt={1} pb={0.1}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Tipo de compra*
                </SoftTypography>
                {["Compras", "Solicitante"].includes(mode) && (
                  <Tooltip
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                          p: 1,
                          bgcolor: "common.white",
                          "& .MuiTooltip-arrow": {
                            color: "common.white",
                          },
                          boxShadow: 5,
                        },
                      },
                    }}
                    title={
                      <SoftBox textAlign="left">
                        <SoftTypography variant="button">Proceso de compra</SoftTypography>
                        <br />
                        <SoftTypography variant="caption">
                          Tipos de compra segun limites
                        </SoftTypography>
                        {["Directa", "Concurso"]
                          .filter((tipo) => describirIntervalo(tipo, montos))
                          .map((tipo) => (
                            <SoftBox key={tipo}>
                              <SoftTypography variant="caption" fontWeight="bold">
                                {TIPOS_COMPRA[tipo].etiqueta}: &nbsp;
                              </SoftTypography>
                              <SoftTypography variant="caption">
                                ({describirIntervalo(tipo, montos)})
                              </SoftTypography>
                            </SoftBox>
                          ))}
                        {Number(montos?.concurso) > 0 && (
                          <SoftTypography variant="caption" color="secondary">
                            Monto de referencia para proceso de compra: $
                            {formatMoneyPunto(montos.concurso)}
                          </SoftTypography>
                        )}
                      </SoftBox>
                    }
                  >
                    <Icon size="medium" sx={{ cursor: "pointer", opacity: 0.8 }}>
                      help
                    </Icon>
                  </Tooltip>
                )}
              </SoftBox>
            </InputLabel>
            {allowEdit ? (
              <Tooltip title={getError("tipoCompra") || ""} placement="right">
                <Select
                  name="tipoCompra"
                  value={data?.tipoCompra || -1}
                  error={Boolean(getError("tipoCompra"))}
                  sx={{ mt: 0.6 }}
                  onChange={(e) =>
                    handleChange({
                      ...e,
                      target: { ...e.target, value: e.target.value === -1 ? null : e.target.value },
                    })
                  }
                  disabled={!allowTipoCompra}
                >
                  <MenuItem value={-1} disabled>
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="98%"
                    >
                      Seleccionar tipo de compra
                    </SoftBox>
                  </MenuItem>
                  {Object.entries(TIPOS_COMPRA).map(([key, { etiqueta, icon, color }]) => (
                    <MenuItem key={key} value={key}>
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="98%"
                      >
                        {etiqueta}
                        <Icon fontSize="small" sx={{ color: getUproColor(color) }}>
                          {icon}
                        </Icon>
                      </SoftBox>
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip>
            ) : mode === "Compras" ? (
              <SoftBox display="flex" alignItems="center" gap={0.5}>
                <Icon
                  fontSize="small"
                  sx={{ color: getUproColor(tipoCompraActual?.color || "uproGreen") }}
                >
                  {tipoCompraActual?.icon || "help_outline"}
                </Icon>
                <SoftTypography variant="button" fontWeight="bold">
                  {data?.tipoCompra ?? "Por proceso de compra"}
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox>
                <SoftTypography variant="button" fontWeight="bold">
                  {tipoCompraActual?.procesoCompra ? "Por proceso de compra" : data?.tipoCompra}
                </SoftTypography>
              </SoftBox>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <InputLabel htmlFor="fechaSolicitud">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Fecha solicitud*
              </SoftTypography>
            </InputLabel>
            {allowEdit ? (
              <Tooltip title={getError("fechaSolicitud") || ""} placement="right">
                <SoftInput
                  label="fechaSolicitud"
                  type="date"
                  name="fechaSolicitud"
                  error={Boolean(getError("fechaSolicitud"))}
                  value={data?.fechaSolicitud || ""}
                  onChange={handleChange}
                  inputProps={!data?.id ? { min: dayjs().format("YYYY-MM-DD") } : undefined}
                />
              </Tooltip>
            ) : (
              <SoftTypography variant="button" fontWeight="regular">
                {data?.fechaSolicitud ? formatDate(data?.fechaSolicitud, "dd/MM/yyyy") : "-"}
              </SoftTypography>
            )}
          </Grid>
          <Grid item xs={allowEdit ? 4 : 2}>
            <InputLabel htmlFor="fechaEntrega">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Fecha de Entrega*
              </SoftTypography>
            </InputLabel>
            {allowEdit ? (
              <Tooltip title={getError("fechaEntrega") || ""} placement="right">
                <SoftInput
                  label="fechaEntrega"
                  placeholder="Fecha de Entrega"
                  type="date"
                  name="fechaEntrega"
                  error={Boolean(getError("fechaEntrega"))}
                  value={data?.fechaEntrega || ""}
                  onChange={handleChange}
                />
              </Tooltip>
            ) : (
              <SoftTypography variant="caption">
                {data?.fechaEntrega ? formatDate(data?.fechaEntrega, "dd/MM/yyyy") : "-"}
              </SoftTypography>
            )}
          </Grid>
          {puedeElegirResponsable && (
            <Grid item xs={12} sm={4}>
              <InputLabel htmlFor="responsable">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Responsable consultas técnicas*
                </SoftTypography>
              </InputLabel>
              {allowEdit ? (
                <Tooltip title={getError("idResponsable") || ""} placement="right">
                  <Autocomplete
                    name="idResponsable"
                    onChange={(e, value) => {
                      handleChange({ target: { name: "idResponsable", value: value?.id || "" } });
                    }}
                    options={dataUsers?.usuarios || []}
                    value={selectedResponsable}
                    getOptionLabel={(option) => `${option.nombre} ${option.apellido}`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Buscar usuario..."
                        error={Boolean(getError("idResponsable"))}
                        helperText={getError("idResponsable") || ""}
                      />
                    )}
                    noOptionsText="No hay resultados"
                  />
                </Tooltip>
              ) : (
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon sx={{ color: getUproColor("uproGreen") }}>person_pin</Icon>
                  <SoftTypography variant="button" fontWeight="regular">
                    {selectedResponsable
                      ? selectedResponsable?.nombre + " " + selectedResponsable?.apellido
                      : "No seleccionado"}
                  </SoftTypography>
                </SoftBox>
              )}
            </Grid>
          )}
          <Grid item xs={12} sm={4} ml={puedeElegirResponsable ? 2 : 0}>
            <InputLabel htmlFor="solicitante">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Persona que solicita
              </SoftTypography>
            </InputLabel>
            <SoftBox display="flex" alignItems="center" gap={0.5} mt={1}>
              <Icon sx={{ color: getUproColor("uproGreen") }}>emoji_people</Icon>
              <SoftTypography variant="button" fontWeight="regular">
                {(data?.createdUser ?? currentUser)?.nombre}{" "}
                {(data?.createdUser ?? currentUser)?.apellido}
              </SoftTypography>
            </SoftBox>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={4}
        sx={{
          display: allowProcesoCotizacion() ? "block" : "none",
        }}
      >
        <DinamicCard
          title="Proceso de cotización"
          data={{
            estado: data?.licitacionEstado || "-",
            inicio: data?.licitacionInicio
              ? formatDate(data?.licitacionInicio, "dd/MM/yy hh:mm (XXXX)")
              : "-",
            fin: data?.licitacionFin
              ? formatDate(data?.licitacionFin, "dd/MM/yy hh:mm (XXXX)")
              : "-",
          }}
        >
          <SoftBox display="flex" flexDirection="column-reverse" height="100%">
            {(tienePermisoCompras(currentUser, verificarAcceso, PERMISOS_COMPRAS.GESTIONAR_COTIZACION) || ["Finalizada", "Cerrada"].includes(data?.licitacionEstado)) && (
              <Tooltip
                title={
                  data?.stockEstado !== "Verificado" && data?.tipoDetalle !== "Servicios"
                    ? "Se requiere verificación del almacén"
                    : ""
                }
                placement="top"
              >
                <span>
                  <SoftButton
                    disabled={
                      data?.stockEstado !== "Verificado" && data?.tipoDetalle !== "Servicios"
                    }
                    color="uproYellow"
                    circular
                    sx={{ marginTop: 1 }}
                    onClick={() => {
                      setOpenIniciarLicitacion(true);
                    }}
                    fullWidth
                  >
                    {!data?.licitacionInicio ? "Iniciar" : "Ver detalles"}
                    &nbsp;<Icon>flag</Icon>
                  </SoftButton>
                </span>
              </Tooltip>
            )}
          </SoftBox>
        </DinamicCard>
      </Grid>

      <Grid item xs={12}>
        <InputLabel htmlFor="objetoCompra">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Objeto de compra*
          </SoftTypography>
        </InputLabel>
        {allowEdit ? (
          <Tooltip title={getError("objetoCompra") || ""} placement="right">
            <SoftInput
              label="objetoCompra"
              placeholder="Objeto de compra"
              type="text"
              name="objetoCompra"
              rows={3}
              multiline
              error={Boolean(getError("objetoCompra"))}
              value={data?.objetoCompra || ""}
              onChange={handleChange}
              inputProps={{ maxLength: 2048 }}
            />
          </Tooltip>
        ) : (
          <SoftTypography variant="caption">{data?.objetoCompra || "-"}</SoftTypography>
        )}
      </Grid>

      <Grid item xs={12}>
        <InputLabel htmlFor="justificacion">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Justificación de compra
          </SoftTypography>
        </InputLabel>
        {allowEdit ? (
          <SoftInput
            label="justificacion"
            placeholder="Motivo por el que se necesita la compra"
            type="text"
            name="justificacion"
            rows={2}
            multiline
            value={data?.justificacion || ""}
            onChange={handleChange}
            inputProps={{ maxLength: 20480 }}
          />
        ) : (
          <SoftTypography variant="caption">{data?.justificacion || "-"}</SoftTypography>
        )}
      </Grid>

      <ModalDetalleLicitacion
        open={openIniciarLicitacion}
        handleClose={() => {
          setOpenIniciarLicitacion(false);
        }}
        idPedidoCompra={data?.id}
        refetchParent={() => {
          refetchParent();
          refetch();
        }}
        mode={mode}
      />
    </Grid>
  );
}
